import motion from '@lib/motion';
import {
  getObserver,
  layoutRecalcManager,
  layoutPhaseMeasure,
  layoutPhaseApply,
  debounce,
  lockScroll,
  trapFocus,
  removeTrapFocus,
  closeDrawer,
  createSlider,
  setDrawerOpenAccessibility,
  restoreFocusTo,
  getFocusableElements,
} from '@theme/theme-global';

const MOBILE_CARD_WIDTH = 220;
const DESKTOP_CARD_WIDTH = 235;
const MOBILE_BREAKPOINT = 768;

const SMG_FOCUSABLE_SELECTOR =
  'a[href], area[href], button, input:not([type="hidden"]), select, textarea, summary, iframe, object, [tabindex], [contenteditable="true"]';

class SocialMediaPopup extends HTMLElement {
  constructor() {
    super();
    this._closeHandlersBound = false;
    this._openerElement = null;
    this._restoreFocusOnClose = true;
    this._sliderEventsBound = false;
    this._focusTimer = null;
    this._desktopMq = null;
    this._boundDrawerClose = this._onDrawerClose.bind(this);
    this._boundEscapeKeydown = this._onEscapeKeydown.bind(this);
    this._boundBreakpointChange = this._onBreakpointChange.bind(this);
    this._boundTabKeydown = this._onTabKeydown.bind(this);
    this._onTransitionEnd = this._onTransitionEnd.bind(this);
  }

  connectedCallback() {
    if (!this.hasAttribute('data-drawer')) {
      this.setAttribute('data-drawer', 'social-media-popup');
    }
    this._bindCloseHandlers();
    this.addEventListener('drawer:close', this._boundDrawerClose);
    document.addEventListener('keydown', this._boundEscapeKeydown);
    // Capture so we run before the global trapFocus Tab handler
    document.addEventListener('keydown', this._boundTabKeydown, true);
    this._desktopMq = window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT}px)`);
    if (this._desktopMq.addEventListener) {
      this._desktopMq.addEventListener('change', this._boundBreakpointChange);
    } else if (this._desktopMq.addListener) {
      this._desktopMq.addListener(this._boundBreakpointChange);
    }
    this._bindSliderEvents();
  }

  disconnectedCallback() {
    this.removeEventListener('drawer:close', this._boundDrawerClose);
    document.removeEventListener('keydown', this._boundEscapeKeydown);
    document.removeEventListener('keydown', this._boundTabKeydown, true);
    if (this._desktopMq?.removeEventListener) {
      this._desktopMq.removeEventListener('change', this._boundBreakpointChange);
    } else if (this._desktopMq?.removeListener) {
      this._desktopMq.removeListener(this._boundBreakpointChange);
    }
    this._clearPendingFocus();
    this._sliderEventsBound = false;
  }

  _getMarquee() {
    return this.closest('.social-media-gallery')?.querySelector('scatter-marquee');
  }

  _clearPendingFocus() {
    clearTimeout(this._focusTimer);
    this._focusTimer = null;
    this.removeEventListener('transitionend', this._onTransitionEnd);
  }

  _onTransitionEnd(event) {
    if (event.target !== this && !event.target?.classList?.contains('drawer-inner')) return;
    this._clearPendingFocus();
    this._trapDrawerFocus();
  }

  _getActiveSlide() {
    const swiper = this._getSwiper();
    if (!swiper?.slides?.length) return null;
    return swiper.slides[swiper.activeIndex] || this.querySelector('.swiper-slide-active');
  }

  _getActiveProductFocusables() {
    const slide = this._getActiveSlide();
    if (!slide) return [];
    const productSwiper = slide.querySelector('.social-media-popup-product-swiper') || slide;
    return getFocusableElements(productSwiper);
  }

  _getInitialFocusTarget() {
    const products = this._getActiveProductFocusables();
    if (products.length) return products[0];
    return this.querySelector('.drawer-close-btn') || this.querySelector('.drawer-inner') || this;
  }

  _isNaturalTabStop(el) {
    if (!el || el.disabled || el.getAttribute('aria-disabled') === 'true') return false;
    const tag = el.tagName;
    if (tag === 'A') return el.hasAttribute('href');
    if (tag === 'BUTTON' || tag === 'SUMMARY' || tag === 'SELECT' || tag === 'TEXTAREA') return true;
    if (tag === 'INPUT') return el.type !== 'hidden';
    if (tag === 'IFRAME' || tag === 'OBJECT' || tag === 'AREA') return true;
    if (el.isContentEditable) return true;
    return false;
  }

  _setElementTabbable(el, enabled) {
    if (!el) return;
    if (enabled) {
      if (el.hasAttribute('data-smg-tabindex')) {
        const prev = el.getAttribute('data-smg-tabindex');
        if (prev === '') el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', prev);
        el.removeAttribute('data-smg-tabindex');
        return;
      }
      // Second open: a11y may have restored stale tabindex=-1 saved while slide was inactive
      if (this._isNaturalTabStop(el) && el.getAttribute('tabindex') === '-1') {
        el.removeAttribute('tabindex');
      }
      return;
    }
    if (!el.hasAttribute('data-smg-tabindex')) {
      el.setAttribute('data-smg-tabindex', el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : '');
    }
    el.setAttribute('tabindex', '-1');
  }

  _setContainerTabbable(container, enabled) {
    if (!container) return;
    container.querySelectorAll(SMG_FOCUSABLE_SELECTOR).forEach((el) => {
      this._setElementTabbable(el, enabled);
    });
  }

  _syncSlideTabbables() {
    if (!this.hasAttribute('open') && !this.hasAttribute('active')) return;

    const swiper = this._getSwiper();
    if (!swiper?.slides?.length) return;

    swiper.slides.forEach((slide) => {
      const isActive = slide.classList.contains('swiper-slide-active');
      this._setContainerTabbable(slide, isActive);
    });

    // Main gallery bullets stay out of Tab order — slides move via product Tab edge
    const mainSlider = this.querySelector('.social-media-popup-swiper');
    mainSlider?.querySelectorAll(':scope > .swiper-pagination .swiper-pagination-bullet').forEach((el) => {
      this._setElementTabbable(el, false);
    });
  }

  _restoreAllSlideTabbables() {
    this.querySelectorAll('[data-smg-tabindex]').forEach((el) => {
      this._setElementTabbable(el, true);
    });
  }

  _focusAdjacentSlideProducts(direction, hopsLeft = null) {
    const swiper = this._getSwiper();
    if (!swiper?.slides?.length) return;

    const remaining = hopsLeft == null ? swiper.slides.length : hopsLeft;
    if (remaining <= 0) {
      this.querySelector('.drawer-close-btn')?.focus();
      return;
    }

    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      this._syncSlideTabbables();
      this._handleActiveSlideMedia();

      const products = this._getActiveProductFocusables();
      if (products.length) {
        const target = direction > 0 ? products[0] : products[products.length - 1];
        target.focus({ preventScroll: false });
        return;
      }

      // Skip slides with no products
      this._focusAdjacentSlideProducts(direction, remaining - 1);
    };

    swiper.once('slideChangeTransitionEnd', settle);

    if (direction > 0) swiper.slideNext();
    else swiper.slidePrev();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (settled) return;
        if (!swiper.animating) settle();
      });
    });
  }

  _onTabKeydown(event) {
    if (event.code !== 'Tab') return;
    if (!this.hasAttribute('open') && !this.hasAttribute('active')) return;

    const active = document.activeElement;
    if (!active || !this.contains(active)) return;

    const products = this._getActiveProductFocusables();
    if (!products.length) return;

    const first = products[0];
    const last = products[products.length - 1];

    // Tab past last product on this slide → next main slide → its products
    if (!event.shiftKey && (active === last || last.contains?.(active))) {
      event.preventDefault();
      event.stopPropagation();
      this._focusAdjacentSlideProducts(1);
      return;
    }

    // Shift+Tab on first product → close (so X is reachable; Escape also works)
    if (event.shiftKey && (active === first || first.contains?.(active))) {
      const closeBtn = this.querySelector('.drawer-close-btn');
      if (!closeBtn) return;
      event.preventDefault();
      event.stopPropagation();
      closeBtn.focus();
    }
  }

  _trapDrawerFocus() {
    if (!this.hasAttribute('active')) return;
    this._syncSlideTabbables();
    const focusTarget = this._getInitialFocusTarget();
    trapFocus(this, focusTarget);
  }

  _setOpenerExpanded(expanded) {
    const opener = this._openerElement;
    if (!opener || typeof opener.setAttribute !== 'function') return;
    if (!opener.hasAttribute('data-popup-trigger') && !opener.hasAttribute('aria-expanded')) return;
    opener.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  _onEscapeKeydown(event) {
    if (event.code !== 'Escape') return;
    if (!this.hasAttribute('open') && !this.hasAttribute('active')) return;

    const active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement && !this.contains(active)) {
      return;
    }

    event.preventDefault();
    this.close();
  }

  _onBreakpointChange(event) {
    if (event.matches) return;
    if (!this.hasAttribute('open') && !this.hasAttribute('active')) return;
    this.close({ restoreFocus: false });
  }

  _onDrawerClose() {
    this._pauseAllPopupVideos();
    this._clearPendingFocus();
    this._restoreAllSlideTabbables();

    const opener = this._openerElement;
    const restoreFocus = this._restoreFocusOnClose !== false;
    this._openerElement = null;
    this._restoreFocusOnClose = true;

    if (opener && typeof opener.setAttribute === 'function') {
      if (opener.hasAttribute('data-popup-trigger') || opener.hasAttribute('aria-expanded')) {
        opener.setAttribute('aria-expanded', 'false');
      }
    }

    // Detach trap FIRST — restoring while trap is active steals focus back into the popup
    removeTrapFocus(null, { restoreFocus: false });

    if (restoreFocus && opener) {
      restoreFocusTo(opener);
    }

    const marquee = this._getMarquee();
    if (marquee?.resumeMarqueeForPopup) {
      marquee.resumeMarqueeForPopup();
    }
  }

  _bindSliderEvents() {
    if (this._sliderEventsBound) return;
    const sliderEl = this.querySelector('[data-social-media-slider-element]');
    if (!sliderEl) return;
    this._sliderEventsBound = true;
    createSlider(sliderEl, JSON.parse(sliderEl.dataset.params)).then((swiper) => {
      if (!swiper) return;
      swiper.on('slideChangeTransitionEnd', () => {
        this._handleActiveSlideMedia();
        if (this.hasAttribute('open')) this._syncSlideTabbables();
      });
    });
  }

  _pauseAllPopupVideos() {
    this.querySelectorAll('sky-lazy-media').forEach((mediaEl) => {
      if (typeof mediaEl.pauseMedia === 'function') {
        mediaEl.pauseMedia();
      }

      const poster = mediaEl.querySelector('.video-poster');
      if (poster) {
        poster.classList.remove('hidden');
        poster.style.opacity = '';
        poster.style.transition = '';
      }
    });
  }

  _handleActiveSlideMedia() {
    if (!this.hasAttribute('open')) return;

    this._pauseAllPopupVideos();

    const swiper = this._getSwiper();
    if (!swiper?.slides?.length) return;

    const activeSlide = swiper.slides[swiper.activeIndex];
    const deferredMedia = activeSlide?.querySelector('sky-lazy-media');

    if (!deferredMedia || typeof deferredMedia.loadAndPlayMedia !== 'function') return;

    if (!deferredMedia.media) {
      deferredMedia.loaded = false;
    }

    deferredMedia._playTrigger = 'autoplay';
    deferredMedia.loadAndPlayMedia();
  }

  _bindCloseHandlers() {
    if (this._closeHandlersBound) return;
    this._closeHandlersBound = true;

    this.querySelectorAll('[is="close-drawer"]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.close();
      });
    });
  }

  _getSwiper() {
    const sliderEl = this.querySelector('.social-media-popup-swiper');
    return sliderEl?.swiper || null;
  }

  _goToSlide(slideIndex) {
    const swiper = this._getSwiper();
    if (!swiper) return;

    if (swiper.params.loop && typeof swiper.slideToLoop === 'function') {
      swiper.slideToLoop(slideIndex, 0);
    } else {
      swiper.slideTo(slideIndex, 0);
    }
  }

  close(options = {}) {
    if (!this.hasAttribute('open') && !this.hasAttribute('active')) return;

    this._clearPendingFocus();
    this._restoreFocusOnClose = options.restoreFocus !== false;
    // Restore natural tabindex BEFORE a11y snapshot, otherwise inactive slides
    // get saved as tabindex=-1 and stay stuck after the next open.
    this._restoreAllSlideTabbables();
    closeDrawer(this);
  }

  open(slideIndex = 0, opener = null) {
    if (opener === document.body || opener === document.documentElement) {
      opener = null;
    }
    if (opener) {
      this._openerElement = opener;
    } else if (!this._openerElement) {
      const active = document.activeElement;
      if (active && active !== document.body && active !== document.documentElement && !this.contains(active)) {
        this._openerElement = active;
      }
    }
    this._restoreFocusOnClose = true;
    this._setOpenerExpanded(true);

    const marquee = this._getMarquee();
    if (marquee?.pauseMarqueeForPopup) {
      marquee.pauseMarqueeForPopup();
    }

    setDrawerOpenAccessibility(this);
    this.setAttribute('active', '');
    this.setAttribute('open', '');

    this._clearPendingFocus();
    requestAnimationFrame(() => {
      this._trapDrawerFocus();
    });
    this.addEventListener('transitionend', this._onTransitionEnd);
    this._focusTimer = setTimeout(() => {
      this._clearPendingFocus();
      this._trapDrawerFocus();
    }, 400);

    lockScroll();

    const playActiveSlideMedia = () => this._handleActiveSlideMedia();

    const syncSlide = () => {
      const swiper = this._getSwiper();
      if (!swiper) return false;

      this._goToSlide(slideIndex);
      this._syncSlideTabbables();
      return true;
    };

    const scheduleActiveSlideMedia = () => {
      playActiveSlideMedia();

      const swiper = this._getSwiper();
      if (swiper) {
        swiper.once('slideChangeTransitionEnd', playActiveSlideMedia);
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(playActiveSlideMedia);
      });
    };

    if (syncSlide()) {
      scheduleActiveSlideMedia();
    } else {
      requestAnimationFrame(() => {
        if (syncSlide()) {
          scheduleActiveSlideMedia();
        }
      });
    }

    if (!this._sliderEventsBound) {
      this._bindSliderEvents();
    }
  }
}

if (!customElements.get('social-media-popup')) {
  customElements.define('social-media-popup', SocialMediaPopup);
}

class ScatterMarquee extends HTMLElement {
  constructor() {
    super();

    this.CONFIG = {
      deckOverlap: 15,
      scatterDuration: 0.5,
      rotationRange: 16,
    };
    this.isScattered = false;
    this.isTransitioning = false;
    this.cloned = false;
    this._metrics = null;
    this._scatterObserver = null;
    this._resizeHandler = null;
    this._boundHandleScatter = null;
    this._boundLinkClick = null;
    this._boundCardClick = null;
    this._boundPauseMarquee = null;
    this._boundResumeMarquee = null;
    this._cardClicksBound = false;
    this._marqueePauseCount = 0;
    this._marqueePausedByPopup = false;
    this._cachedCardWidth = null;
    this._cachedCardHeight = null;
    this._cachedContainerWidth = null;
    this._trackHeight = null;
    this._pendingMetrics = null;
    this._pendingCardHeight = null;

    this.track = null;
    this.cards = [];
    this.allCards = [];

    this.initDeck = this.initDeck.bind(this);
    this.handleScatter = this.handleScatter.bind(this);
    this.recalculateLayout = this.recalculateLayout.bind(this);
    this.updateTrackHeight = this.updateTrackHeight.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector('[data-marquee-track]');
    if (!this.track) return;

    this._removeClones();
    this.cloned = false;
    this.cards = Array.from(this.track.querySelectorAll('[data-card]:not([data-clone])'));
    if (this.cards.length === 0) return;

    this.direction = this.dataset.direction || 'ltr';
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';

    this._boundHandleScatter = this.handleScatter.bind(this);
    this._boundLinkClick = this._handleLinkClick.bind(this);

    this.track.addEventListener('click', this._boundHandleScatter);
    this.track.addEventListener('click', this._boundLinkClick);

    this._resizeHandler = debounce(() => {
      if (this.isScattered || this.isTransitioning) return;
      this._syncClones();
    }, 150);

    window.addEventListener('resize', this._resizeHandler);

    // Single batched path: register + one request (no sync-then-request double fire)
    requestAnimationFrame(() => {
      layoutRecalcManager.register(this.recalculateLayout, { root: this });
      layoutRecalcManager.request(this.recalculateLayout);
      this._initVideoHover();
      this._initPauseOnHover();
    });

    this._scatterObserver = getObserver({ rootMargin: '0px 0px -60% 0px', threshold: 0 }, true, false);
    this._scatterObserver.observe(this, (entry) => {
      if (entry.isIntersecting) {
        this.handleScatter();
      }
    });
  }

  disconnectedCallback() {
    layoutRecalcManager.unregister(this.recalculateLayout);
    this._scatterObserver?.unobserve(this);
    this._scatterObserver = null;

    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }

    if (this.track) {
      if (this._boundHandleScatter) {
        this.track.removeEventListener('click', this._boundHandleScatter);
      }
      if (this._boundLinkClick) {
        this.track.removeEventListener('click', this._boundLinkClick);
      }
      if (this._boundCardClick) {
        this.track.removeEventListener('click', this._boundCardClick);
      }
      if (this._boundPauseMarquee) {
        this.track.removeEventListener('mouseenter', this._boundPauseMarquee);
      }
      if (this._boundResumeMarquee) {
        this.track.removeEventListener('mouseleave', this._boundResumeMarquee);
      }
    }
  }

  _refreshLayoutReads() {
    const spacingValue = getComputedStyle(this).getPropertyValue('--marqueeGap');
    const spacing = parseFloat(spacingValue);
    this.spacing = Number.isFinite(spacing) ? spacing : 0;

    let cardWidth = 0;
    if (this.cards[0]) cardWidth = this.cards[0].offsetWidth;
    this._cachedCardWidth =
      cardWidth > 0
        ? cardWidth
        : window.innerWidth <= MOBILE_BREAKPOINT
          ? MOBILE_CARD_WIDTH
          : DESKTOP_CARD_WIDTH;

    this._cachedContainerWidth = this.offsetWidth || this.track?.offsetWidth || window.innerWidth;

    const sampleCard = this.cards.find((card) => card.offsetHeight > 0) || this.cards[0];
    this._cachedCardHeight = sampleCard?.offsetHeight || 0;
  }

  _getCardWidth() {
    if (this._cachedCardWidth != null) return this._cachedCardWidth;
    return window.innerWidth <= MOBILE_BREAKPOINT ? MOBILE_CARD_WIDTH : DESKTOP_CARD_WIDTH;
  }

  _getContainerWidth() {
    if (this._cachedContainerWidth != null) return this._cachedContainerWidth;
    return this.offsetWidth || this.track?.offsetWidth || window.innerWidth;
  }

  _calculateMetrics() {
    const originalCount = this.cards.length;
    const cardWidth = this._getCardWidth();
    const step = cardWidth + (this.spacing || 0);
    const originalTotalWidth = originalCount * step;
    const containerWidth = this._getContainerWidth();

    const K = Math.max(1, Math.ceil(containerWidth / originalTotalWidth));
    const cloneGroups = Math.max(1, 2 * K - 1);
    const scrollDistance = K * originalTotalWidth;

    return {
      originalCount,
      cardWidth,
      step,
      originalTotalWidth,
      containerWidth,
      K,
      cloneGroups,
      scrollDistance,
    };
  }

  _metricsChanged(nextMetrics) {
    if (!this._metrics) return true;
    return (
      nextMetrics.cloneGroups !== this._metrics.cloneGroups ||
      nextMetrics.K !== this._metrics.K ||
      nextMetrics.step !== this._metrics.step
    );
  }

  _removeClones() {
    this.track?.querySelectorAll('[data-card][data-clone]').forEach((clone) => clone.remove());
  }

  _applySyncClones(metrics) {
    const needsRebuild = !this.cloned || this._metricsChanged(metrics);

    this._metrics = metrics;

    if (needsRebuild) {
      this._removeClones();
      this.cloned = false;
      this._buildClones(metrics);
    }

    if (!this.isScattered && !this.isTransitioning) {
      requestAnimationFrame(() => this.initDeck());
    }
  }

  _applyTrackHeight(height) {
    if (!this.track || !height || height <= 0) return;
    if (height === this._trackHeight) return;
    this._trackHeight = height;
    this.track.style.height = `${height}px`;
  }

  _syncClones() {
    this._refreshLayoutReads();
    this._applySyncClones(this._calculateMetrics());
    this._applyTrackHeight(this._cachedCardHeight);
  }

  _buildClones(metrics) {
    if (this.cloned) return;
    this.cloned = true;

    const { originalCount, cloneGroups } = metrics;
    const fragment = document.createDocumentFragment();

    for (let group = 0; group < cloneGroups; group += 1) {
      for (let i = 0; i < originalCount; i += 1) {
        const clone = this.cards[i].cloneNode(true);
        clone.setAttribute('data-clone', 'true');
        // Tab/focus off on clones only — keep pointer clicks working
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll(SMG_FOCUSABLE_SELECTOR).forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
        fragment.appendChild(clone);
      }
    }

    this.track.appendChild(fragment);
    this.allCards = Array.from(this.track.querySelectorAll('[data-card]'));
  }

  updateTrackHeight() {
    if (this.cards.length === 0 || !this.track) return;

    if (this._cachedCardHeight == null) {
      const sampleCard = this.cards.find((card) => card.offsetHeight > 0) || this.cards[0];
      this._cachedCardHeight = sampleCard?.offsetHeight || 0;
    }

    this._applyTrackHeight(this._cachedCardHeight);
  }

  recalculateLayout(phase) {
    if (this.isScattered || this.isTransitioning) return;

    if (layoutPhaseMeasure(phase)) {
      this._refreshLayoutReads();
      this._pendingMetrics = this._calculateMetrics();
      this._pendingCardHeight = this._cachedCardHeight;
    }

    if (layoutPhaseApply(phase)) {
      if (!this._pendingMetrics) return;
      this._applySyncClones(this._pendingMetrics);
      this._applyTrackHeight(this._pendingCardHeight);
      this._pendingMetrics = null;
      this._pendingCardHeight = null;
    }
  }

  _getRowStartX(centerX, originalTotalWidth) {
    return centerX - (originalTotalWidth - this.spacing) / 2;
  }

  _getOffscreenTransform(index, rowStartX, step, containerWidth) {
    const targetX = rowStartX + index * step;
    return `translate3d(${targetX + containerWidth}px, 0px, 0px) rotate(0deg) scale(1)`;
  }

  _isActiveDeckCard(index) {
    if (!this._metrics) return false;
    const deckCount = Math.min(5, this._metrics.originalCount);
    return index < deckCount && !this.allCards[index]?.hasAttribute('data-clone');
  }

  initDeck() {
    const cardsToFan = this.allCards.length ? this.allCards : this.cards;
    if (!cardsToFan.length || !this._metrics) return;

    const { originalCount, cardWidth, containerWidth, step, originalTotalWidth } = this._metrics;
    const deckCount = Math.min(5, originalCount);
    const centerX = containerWidth / 2;
    const baseX = centerX - cardWidth / 2;
    const rowStartX = this._getRowStartX(centerX, originalTotalWidth);

    cardsToFan.forEach((card, i) => {
      card.style.left = '0px';
      card.style.top = '0px';

      if (this._isActiveDeckCard(i)) {
        const deckI = i;
        const factor = (deckCount - 1) / 2 - deckI;
        const ratio = deckCount > 1 ? (deckCount - 1 - deckI) / (deckCount - 1) : 0;
        const x = baseX + factor * this.CONFIG.deckOverlap;
        const y = ratio * ratio * 24;
        const rot = ratio * this.CONFIG.rotationRange;
        const transformStr = `translate3d(${x}px, ${y}px, 0px) rotate(${rot}deg) scale(0.92)`;

        card.style.transform = transformStr;
        card.dataset.fannedTransform = transformStr;
        card.style.zIndex = String(10 + deckI);
        card.style.pointerEvents = 'auto';
      } else {
        const transformStr = this._getOffscreenTransform(i, rowStartX, step, containerWidth);

        card.style.transform = transformStr;
        card.dataset.fannedTransform = transformStr;
        card.style.zIndex = '1';
        card.style.pointerEvents = 'none';
      }
    });
  }

  _getDeckCenterIndex(deckCount) {
    return Math.floor((deckCount - 1) / 2);
  }

  _getCenterStackTransform(deckCount) {
    const centerDeckIndex = this._getDeckCenterIndex(deckCount);
    const centerCard = this.allCards[centerDeckIndex];
    return centerCard?.dataset.fannedTransform || centerCard?.style.transform || '';
  }

  _getOriginalScatterDelay(originalIndex, originalCount, baseDuration) {
    const halfCount = Math.floor(originalCount / 2);
    const staggerDelay = baseDuration * 0.035;

    if (originalIndex < halfCount) {
      const distanceFromCenter = halfCount - 1 - originalIndex;
      return distanceFromCenter * staggerDelay;
    }

    const distanceFromCenter = originalIndex - halfCount;
    const rightSideDelayOffset = baseDuration * 0.6;
    return rightSideDelayOffset + distanceFromCenter * staggerDelay;
  }

  _getScatterStartTransform(originalIndex, deckCount, centerStackTransform) {
    if (originalIndex < deckCount) {
      const card = this.allCards[originalIndex];
      return card?.dataset.fannedTransform || card?.style.transform || centerStackTransform;
    }

    return centerStackTransform;
  }

  _runScatterAnimation(card, startTransform, endTransform, delay, baseDuration, easing, animate) {
    card.style.transform = startTransform;

    if (animate) {
      const animation = animate(
        card,
        { transform: [startTransform, endTransform] },
        {
          duration: baseDuration,
          delay,
          easing,
        },
      );

      if (animation?.finished) {
        return animation.finished;
      }

      return this._fallbackAnimationPromise(delay + baseDuration, card, endTransform);
    }

    const webAnimation = card.animate([{ transform: startTransform }, { transform: endTransform }], {
      duration: baseDuration * 1000,
      delay: delay * 1000,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      fill: 'forwards',
    });

    if (webAnimation?.finished) {
      return webAnimation.finished.then(() => {
        card.style.transform = endTransform;
      });
    }

    return this._fallbackAnimationPromise(delay + baseDuration, card, endTransform);
  }

  _handleLinkClick(event) {
    const link = event.target.closest('a');
    if (link && (!this.isScattered || this.isTransitioning)) {
      event.preventDefault();
    }
  }

  _getCardOriginalIndex(card) {
    const originalCount = this.cards.length;
    if (!originalCount) return 0;

    const allCards = this.allCards.length ? this.allCards : this.cards;
    const trackIndex = allCards.indexOf(card);
    if (trackIndex === -1) return 0;

    return trackIndex % originalCount;
  }

  _getPopup() {
    return this.closest('.social-media-gallery')?.querySelector('social-media-popup');
  }

  _handleCardClick(event) {
    if (!this.isScattered || this.isTransitioning) return;

    const card = event.target.closest('[data-card]');
    if (!card) return;

    const popup = this._getPopup();
    if (!popup) return;

    event.preventDefault();

    const opener =
      event.target.closest('[data-popup-trigger="social-media-popup"]') ||
      card.querySelector('[data-popup-trigger="social-media-popup"]') ||
      document.activeElement;

    popup.open(this._getCardOriginalIndex(card), opener);
  }

  _initCardClicks() {
    if (this._cardClicksBound || !this.track) return;
    this._cardClicksBound = true;
    this._boundCardClick = this._handleCardClick.bind(this);
    this.track.addEventListener('click', this._boundCardClick);
  }

  pauseMarquee() {
    if (!this.isScattered || !this.track?.classList.contains('marquee-running')) return;

    this._marqueePauseCount += 1;
    if (this._marqueePauseCount === 1) {
      this.track.style.animationPlayState = 'paused';
    }
  }

  resumeMarquee() {
    if (!this.isScattered || !this.track?.classList.contains('marquee-running')) return;

    this._marqueePauseCount = Math.max(0, this._marqueePauseCount - 1);
    if (this._marqueePauseCount === 0) {
      this.track.style.animationPlayState = 'running';
    }
  }

  pauseMarqueeForPopup() {
    if (this._marqueePausedByPopup) return;
    this._marqueePausedByPopup = true;
    this.pauseMarquee();
  }

  resumeMarqueeForPopup() {
    if (!this._marqueePausedByPopup) return;
    this._marqueePausedByPopup = false;
    this.resumeMarquee();
  }

  _initPauseOnHover() {
    if (!this.pauseOnHover || !window.matchMedia('(hover: hover)').matches) return;

    this._boundPauseMarquee = () => {
      if (this.isScattered) {
        this.pauseMarquee();
      }
    };

    this._boundResumeMarquee = () => {
      if (this.isScattered) {
        this.resumeMarquee();
      }
    };

    this.track.addEventListener('mouseenter', this._boundPauseMarquee);
    this.track.addEventListener('mouseleave', this._boundResumeMarquee);
  }

  _initVideoHover() {
    const cards = this.allCards.length ? this.allCards : this.cards;

    cards.forEach((card) => {
      const deferredMedia = card.querySelector('sky-lazy-media');
      if (!deferredMedia) return;

      let hoverToken = 0;
      let playTimer = null;
      let posterHideTimer = null;

      const getPoster = () => deferredMedia.querySelector('.video-poster');

      const clearPlayTimer = () => {
        if (playTimer) {
          clearTimeout(playTimer);
          playTimer = null;
        }
      };

      const clearPosterHideTimer = () => {
        if (posterHideTimer) {
          clearTimeout(posterHideTimer);
          posterHideTimer = null;
        }
      };

      const restorePoster = () => {
        clearPosterHideTimer();
        const poster = getPoster();
        if (!poster) return;
        poster.classList.remove('hidden');
        poster.style.opacity = '';
        poster.style.transition = '';
      };

      const hidePoster = () => {
        const poster = getPoster();
        if (!poster || poster.classList.contains('hidden')) return;

        poster.style.transition = 'opacity 0.5s ease';
        poster.style.opacity = '0';
        clearPosterHideTimer();
        posterHideTimer = setTimeout(() => {
          posterHideTimer = null;
          if (card.matches(':hover')) {
            poster.classList.add('hidden');
          } else {
            restorePoster();
          }
        }, 500);
      };

      const resetMedia = () => {
        try {
          const video = deferredMedia.querySelector('video');
          if (video) {
            video.currentTime = 0;
            return;
          }

          const iframe = deferredMedia.querySelector('iframe');
          if (!iframe?.src) return;

          if (iframe.src.includes('youtube.com')) {
            iframe.contentWindow?.postMessage('{"event":"command","func":"seekTo","args":[0, true]}', '*');
          } else if (iframe.src.includes('vimeo.com')) {
            iframe.contentWindow?.postMessage('{"method":"setCurrentTime","value":0}', '*');
          }
        } catch (error) {
          console.warn(error);
        }
      };

      const bindVideoHoverHandlers = () => {
        if (deferredMedia.dataset.hoverVideoBound) return;

        const video = deferredMedia.querySelector('video');
        if (!video) return;

        deferredMedia.dataset.hoverVideoBound = 'true';

        video.addEventListener('playing', () => {
          if (card.matches(':hover')) {
            hidePoster();
          } else {
            video.pause();
            restorePoster();
          }
        });

        video.addEventListener('pause', () => {
          if (!card.matches(':hover')) {
            restorePoster();
          }
        });
      };

      const pauseAndRestore = () => {
        if (typeof deferredMedia.pauseMedia === 'function') {
          deferredMedia.pauseMedia();
        }
        resetMedia();
        restorePoster();
      };

      const playMediaIfHovered = (token) => {
        if (token !== hoverToken || !card.matches(':hover')) return;

        const startPlayback = () => {
          if (token !== hoverToken || !card.matches(':hover')) return;

          deferredMedia._playTrigger = 'manual';
          resetMedia();

          if (!deferredMedia.loaded && typeof deferredMedia.loadContent === 'function') {
            deferredMedia.loadContent();
          }

          bindVideoHoverHandlers();

          if (typeof deferredMedia.playMedia === 'function') {
            deferredMedia.playMedia();
          }

          const video = deferredMedia.querySelector('video');
          if (video && video.readyState >= 2 && card.matches(':hover') && !video.paused) {
            hidePoster();
          }
        };

        if (typeof deferredMedia.playMedia === 'function') {
          startPlayback();
          return;
        }

        customElements.whenDefined('sky-lazy-media').then(() => {
          startPlayback();
        });
      };

      card.addEventListener('mouseenter', () => {
        hoverToken += 1;
        const token = hoverToken;
        clearPlayTimer();
        playTimer = setTimeout(() => {
          playTimer = null;
          playMediaIfHovered(token);
        }, 50);
      });

      card.addEventListener('mouseleave', () => {
        hoverToken += 1;
        clearPlayTimer();
        pauseAndRestore();
      });
    });
  }

  handleScatter() {
    if (this.isTransitioning || this.isScattered) return;

    this.track.removeEventListener('click', this._boundHandleScatter);
    this.isTransitioning = true;
    this.classList.add('scattered', 'is-scattering');

    if (!this._metrics) {
      this._refreshLayoutReads();
      this._metrics = this._calculateMetrics();
    }

    const { originalCount, step, originalTotalWidth, containerWidth, scrollDistance } = this._metrics;
    const centerX = containerWidth / 2;
    const rowStartX = centerX - (originalTotalWidth - this.spacing) / 2;
    const animate = motion?.animate;
    const baseDuration = this.CONFIG.scatterDuration;
    const easing = [0.16, 1, 0.3, 1];
    const animations = [];
    const deckCount = Math.min(5, originalCount);
    const centerStackTransform = this._getCenterStackTransform(deckCount);

    for (let originalIndex = deckCount; originalIndex < originalCount; originalIndex += 1) {
      const card = this.allCards[originalIndex];
      if (!card || card.hasAttribute('data-clone')) continue;

      card.style.transform = centerStackTransform;
      card.style.zIndex = '5';
      card.style.pointerEvents = 'none';
    }

    requestAnimationFrame(() => {
      for (let originalIndex = 0; originalIndex < originalCount; originalIndex += 1) {
        const card = this.allCards[originalIndex];
        if (!card || card.hasAttribute('data-clone')) continue;

        const targetX = rowStartX + originalIndex * step;
        const endTransform = `translate3d(${targetX}px, 0px, 0px) rotate(0deg) scale(1)`;
        const startTransform = this._getScatterStartTransform(originalIndex, deckCount, centerStackTransform);
        const delay = this._getOriginalScatterDelay(originalIndex, originalCount, baseDuration);

        card.style.zIndex = originalIndex < deckCount ? String(10 + originalIndex) : '8';
        card.style.pointerEvents = originalIndex < deckCount ? 'auto' : 'none';

        animations.push(
          this._runScatterAnimation(card, startTransform, endTransform, delay, baseDuration, easing, animate),
        );
      }

      Promise.all(animations).then(() => {
        for (let originalIndex = 0; originalIndex < originalCount; originalIndex += 1) {
          const card = this.allCards[originalIndex];
          if (!card || card.hasAttribute('data-clone')) continue;

          const targetX = rowStartX + originalIndex * step;
          card.style.transform = `translate3d(${targetX}px, 0px, 0px) rotate(0deg) scale(1)`;
        }

        requestAnimationFrame(() => {
          this.isTransitioning = false;
          this.isScattered = true;
          this.classList.remove('is-scattering');

          this.track.style.setProperty('--marquee-start', `${rowStartX}px`);
          this.track.style.setProperty('--marquee-end', `${rowStartX - scrollDistance}px`);
          this.track.style.setProperty('--marqueeGap', `${this.spacing}px`);

          const speedSetting = Number.parseFloat(this.dataset.speed) || 4;
          const speedPxPerSec = speedSetting * 0.4 * 60;
          const duration = scrollDistance / speedPxPerSec;
          this.track.style.setProperty('--marquee-duration', `${duration}s`);

          const animName = this.direction === 'ltr' ? 'marquee-scroll-ltr' : 'marquee-scroll-rtl';
          this.track.style.setProperty('--marquee-animation', animName);
          this.track.classList.add('marquee-running');
          this.track.style.touchAction = 'none';

          this.allCards.forEach((card) => {
            card.removeAttribute('style');
            card.removeAttribute('data-fanned-transform');
          });

          this._initCardClicks();
        });
      });
    });
  }

  _fallbackAnimationPromise(totalSeconds, card, endTransform) {
    return new Promise((resolve) => {
      window.setTimeout(() => {
        if (card && endTransform) {
          card.style.transform = endTransform;
        }
        resolve();
      }, totalSeconds * 1000);
    });
  }
}

if (!customElements.get('scatter-marquee')) {
  customElements.define('scatter-marquee', ScatterMarquee);
}

if (!customElements.get('slider-social-media-gallery')) {
  class SocialMediaGallerySlider extends HTMLDivElement {
    connectedCallback() {
      // Popup owns init + video sync in SocialMediaPopup
      if (this.closest('social-media-popup')) return;
      if (this.swiper || this._sliderInitializing) return;

      this._sliderInitializing = true;
      const params = JSON.parse(this.dataset.params || '{}');
      if (params.pagination?.el && typeof params.pagination.el === 'string') {
        const paginationEl = this.querySelector(params.pagination.el);
        if (paginationEl) params.pagination.el = paginationEl;
      }

      createSlider(this, params)
        .then((swiper) => {
          if (!swiper) return;
          swiper.on('slideChangeTransitionEnd', () => this._handleActiveSlideMedia());
          requestAnimationFrame(() => this._handleActiveSlideMedia());
        })
        .finally(() => {
          this._sliderInitializing = false;
        });
    }

    _pauseAllVideos() {
      this.querySelectorAll('sky-lazy-media').forEach((mediaEl) => {
        if (typeof mediaEl.pauseMedia === 'function') {
          mediaEl.pauseMedia();
        }

        const poster = mediaEl.querySelector('.video-poster');
        if (poster) {
          poster.classList.remove('hidden');
          poster.style.opacity = '';
          poster.style.transition = '';
        }
      });
    }

    _handleActiveSlideMedia() {
      this._pauseAllVideos();

      const swiper = this.swiper;
      if (!swiper?.slides?.length) return;

      const activeSlide = swiper.slides[swiper.activeIndex];
      const deferredMedia = activeSlide?.querySelector('sky-lazy-media');
      if (!deferredMedia || typeof deferredMedia.loadAndPlayMedia !== 'function') return;

      if (!deferredMedia.media) {
        deferredMedia.loaded = false;
      }

      deferredMedia._playTrigger = 'autoplay';
      deferredMedia.loadAndPlayMedia();
    }
  }

  customElements.define('slider-social-media-gallery', SocialMediaGallerySlider, { extends: 'div' });
}

if (!customElements.get('slider-social-media-products')) {
  class SocialMediaProductSlider extends HTMLDivElement {
    constructor() {
      super();
      this.section = this.closest('.shopify-section');
    }

    connectedCallback() {
      createSlider(this, JSON.parse(this.dataset.params));
    }
  }

  customElements.define('slider-social-media-products', SocialMediaProductSlider, { extends: 'div' });
}
